'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useAuth, useGetMarketBoosterLeaderboardInfinite } from '@morfi/backend-sdk-v2/client';
import { useRef, useState } from 'react';
import { useOwnRowVisibility } from '@/components/market/shared/use-own-row-visibility';
import { InfiniteList } from '@/components/ui/infinite-list';
import { MarketBoostersEmpty } from './market-boosters-empty';
import { MarketBoostersError } from './market-boosters-error';
import { MarketBoostersSkeleton } from './market-boosters-skeleton';
import { MarketBoostersTable } from './market-boosters-table';
import { MarketYourBoosterCard } from './market-your-booster-card';
import type { MarketBoosterLeaderboardEntry, MarketBoosterSortValue } from './types';
interface MarketBoostersTabProps {
  marketPubkey: string;
  marketStatus: string;
  isActive: boolean;
}
const BOOSTERS_SCROLL_SELECTOR = '[data-testid="market-boosters-scroll"]';
export function MarketBoostersTab(t0) {
  const $ = _c(33);
  const {
    marketPubkey,
    marketStatus
  } = t0;
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    activeWallet
  } = useActiveWallet();
  const boostersRootRef = useRef(null);
  const [sortBy, setSortBy] = useState("amount");
  let t1;
  if ($[0] !== marketPubkey || $[1] !== sortBy) {
    t1 = {
      pubkey: marketPubkey,
      sortBy,
      sortOrder: "desc"
    };
    $[0] = marketPubkey;
    $[1] = sortBy;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useGetMarketBoosterLeaderboardInfinite(t1);
  const entries = query.rows as unknown as MarketBoosterLeaderboardEntry[];
  const currentUserId = user?.id ?? null;
  const currentWalletAddress = activeWallet?.address ?? null;
  let t2;
  if ($[3] !== currentWalletAddress || $[4] !== entries) {
    t2 = currentWalletAddress === null ? undefined : entries.find(entry => entry.booster.walletAddress === currentWalletAddress);
    $[3] = currentWalletAddress;
    $[4] = entries;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const ownEntry = t2;
  const ownRowObservationKey = ownEntry === undefined ? null : `${ownEntry.participationId}:${sortBy}`;
  const ownRowVisibility = useOwnRowVisibility(boostersRootRef, ownRowObservationKey, BOOSTERS_SCROLL_SELECTOR, "market.boosters.your_position_observer_failed");
  const showOwnPosition = isAuthenticated && currentUserId !== null && ownEntry !== undefined && ownRowVisibility.observationKey === ownRowObservationKey && !ownRowVisibility.visible;
  if (query.isPending && entries.length === 0) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <MarketBoostersSkeleton />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  if (query.isError && entries.length === 0) {
    let t3;
    if ($[7] !== query) {
      t3 = <MarketBoostersError onRetry={() => void query.refetch()} />;
      $[7] = query;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (!query.isPending && !query.isError && entries.length === 0) {
    let t3;
    if ($[9] !== marketPubkey || $[10] !== marketStatus) {
      t3 = <MarketBoostersEmpty marketPubkey={marketPubkey} marketStatus={marketStatus} />;
      $[9] = marketPubkey;
      $[10] = marketStatus;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  const t3 = query.hasNextPage === true;
  const t4 = query.isFetchingNextPage === true;
  let t5;
  if ($[12] !== showOwnPosition) {
    t5 = showOwnPosition ? {
      className: "pb-20"
    } : {};
    $[12] = showOwnPosition;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== currentWalletAddress || $[15] !== entries || $[16] !== sortBy) {
    t6 = <MarketBoostersTable entries={entries} currentWalletAddress={currentWalletAddress} sortBy={sortBy} onSortChange={setSortBy} />;
    $[14] = currentWalletAddress;
    $[15] = entries;
    $[16] = sortBy;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== query.fetchNextPage || $[19] !== query.isError || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t7 = <InfiniteList hasNextPage={t3} isFetchingNextPage={t4} fetchNextPage={query.fetchNextPage} isError={query.isError} maxHeight={560} {...t5} data-testid="market-boosters-scroll">{t6}</InfiniteList>;
    $[18] = query.fetchNextPage;
    $[19] = query.isError;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== currentUserId || $[26] !== ownEntry || $[27] !== showOwnPosition || $[28] !== sortBy) {
    t8 = ownEntry !== undefined && currentUserId !== null ? <MarketYourBoosterCard entry={ownEntry} userId={currentUserId} sortBy={sortBy} visible={showOwnPosition} /> : null;
    $[25] = currentUserId;
    $[26] = ownEntry;
    $[27] = showOwnPosition;
    $[28] = sortBy;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t7 || $[31] !== t8) {
    t9 = <div className="flex flex-col gap-2"><div ref={boostersRootRef} className="relative">{t7}{t8}</div></div>;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}